import { useState, useEffect } from "react";
import Loader from "components/Loader";
import HelpCard from "components/Cards/HelpCard";
import { Button, Col, Form } from "react-bootstrap";
import Validator from "utils/Validator";
import classNames from "classnames";
import { css } from "aphrodite";
import pageStyles from "styles/pageStyles";
import { getInputFieldStyle, getButtonStyle } from "utils/formStyles";
import InsufficientCreditsPopup from "components/InsufficientCreditsPopup";
import ToastAlert from "components/Alert/ToastAlert";
import { submitAiOverviewByKeywordRequest } from "services/buddyXApiHubService";
import AiOverviewTable from "components/MarkdownRender/tables/AiOverviewTable";

export default function Page() {
  const [isFormValid, setIsFormValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isResultGot, setIsResultGot] = useState(false);
  const [showCopyNotification, setShowCopyNotification] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [keyword, setKeyword] = useState("");
  const [brand, setBrand] = useState("");
  const [noQueries, setNoQueries] = useState(10);
  const [aiOverview, setAiOverview] = useState("");
  const [aiOverviewList, setAiOverviewList] = useState([]);
  const [isListResponse, setIsListResponse] = useState(false);
  const [insufficientCreditsData, setInsufficientCreditsData] = useState(null);
  const [showInsufficientCredits, setShowInsufficientCredits] = useState(false);
  const [copiedRowIndex, setCopiedRowIndex] = useState(null);
  const [errors, setErrors] = useState({
    keyword: "",
    brand: "",
    noQueries: "",
  });

  const firstLevelTopic = "Content Editor";
  const secondLevelTopic = "AI Overview";
  const thirdLevelTopic = "Keyword";

  const handleInsufficientCredits = (balanceData) => {
    setInsufficientCreditsData(balanceData);
    setShowInsufficientCredits(true);
  };

  const closeInsufficientCredits = () => {
    setShowInsufficientCredits(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    switch (name) {
      case "keyword":
        setKeyword(value);
        break;
      case "brand":
        setBrand(value);
        break;
      case "noQueries":
        setNoQueries(parseInt(value) || 1);
        break;
      default:
        break;
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;

    let error = "";
    switch (name) {
      case "keyword":
        if (!Validator.validateSingleKeyword(value)) {
          error = "Please enter a valid keyword";
        }
        break;
      case "brand":
        // Brand is optional, only validate if value is provided
        if (value.trim() && !Validator.validateBrandName(value)) {
          error = "Please enter a valid brand name";
        }
        break;
      case "noQueries":
        const num = parseInt(value);
        if (!num || num < 1 || num > 50) {
          error = "Number of queries must be between 1 and 50";
        }
        break;
      default:
        break;
    }

    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: error,
    }));
  };

  const handleSubmit = async () => {
    if (!keyword.trim() || isLoading) return;

    setIsLoading(true);
    setIsResultGot(false);
    setAiOverview("");
    setErrorMessage("");

    try {
      const response = await submitAiOverviewByKeywordRequest(
        { primary_keyword: keyword, brand: brand, no_queries: noQueries },
        "ai_overview_keyword"
      );

      setAiOverviewList(response);
      setIsListResponse(true);
      setAiOverview("");
      setIsResultGot(true);
    } catch (error) {
      console.error("Error generating AI overview:", error);
      if (error.response?.status === 402) {
        handleInsufficientCredits(error.response.data);
      } else {
        setErrorMessage(
          error.response?.data?.detail ||
            "Failed to generate AI overview. Please try again."
        );
        setShowErrorNotification(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setKeyword("");
    setBrand("");
    setNoQueries(10);
    setAiOverview("");
    setAiOverviewList([]);
    setIsListResponse(false);
    setIsResultGot(false);
    setErrors({ keyword: "", brand: "", noQueries: "" });
    setShowCopyNotification(false);
    setShowErrorNotification(false);
    setErrorMessage("");
    setCopiedRowIndex(null);
  };

  const handleCopy = async () => {
    try {
      const textToCopy = isListResponse
        ? aiOverviewList
            .map((item, index) => {
              if (typeof item === "object" && item !== null && !Array.isArray(item)) {
                const entries = Object.entries(item);
                const qaText = entries.map(([question, answer]) => `Question: ${question}\nAnswer: ${answer}`).join('\n\n');
                return `${index + 1}. ${qaText}`;
              } else if (typeof item === "string") {
                return `${index + 1}. ${item}`;
              } else {
                return `${index + 1}. ${JSON.stringify(item)}`;
              }
            })
            .join("\n\n")
        : aiOverview;
      await navigator.clipboard.writeText(textToCopy);
      setShowCopyNotification(true);
    } catch (err) {
      console.error("Failed to copy content: ", err);
    }
  };

  const handleRowCopy = async (content, index) => {
    try {
      let textToCopy;
      if (typeof content === "object" && content !== null && !Array.isArray(content)) {
        // Handle object with question as key and answer as value
        const entries = Object.entries(content);
        textToCopy = entries.map(([question, answer]) => `Question: ${question}\n\nAnswer: ${answer}`).join('\n\n---\n\n');
      } else if (typeof content === "string") {
        textToCopy = content;
      } else {
        textToCopy = JSON.stringify(content, null, 2);
      }
      
      await navigator.clipboard.writeText(textToCopy);
      setCopiedRowIndex(index);
      setShowCopyNotification(true);

      // Reset the copied row indicator after 2 seconds
      setTimeout(() => {
        setCopiedRowIndex(null);
      }, 2000);
    } catch (err) {
      console.error("Failed to copy row content: ", err);
    }
  };

  // Form validation
  useEffect(() => {
    const isValid = keyword.trim() !== "" && !errors.keyword && !errors.brand && !errors.noQueries;

    setIsFormValid(isValid);
  }, [keyword, brand, noQueries, errors.keyword, errors.brand, errors.noQueries]);

  return (
    <div className={classNames("container-fluid", css(pageStyles.container))}>
      {/* Toast Notifications */}
      <ToastAlert
        show={showCopyNotification}
        message="AI Overview copied!"
        type="success"
        icon="fas fa-check-circle"
        duration={2000}
        onClose={() => setShowCopyNotification(false)}
        position="top-right"
      />

      <ToastAlert
        show={showErrorNotification}
        message={errorMessage}
        type="error"
        icon="fas fa-exclamation-circle"
        duration={5000}
        onClose={() => setShowErrorNotification(false)}
        position="top-right"
      />

      <h1 className="d-flex fw-normal justify-content-start align-items-center fw-bold">
        {secondLevelTopic}
      </h1>
      <p className="fw-normal justify-content-start align-items-center">
        {firstLevelTopic} / <b>{secondLevelTopic}</b> / <b>{thirdLevelTopic}</b>
      </p>
      <p className="fw-normal justify-content-start align-items-center">
        Generate comprehensive <b>AI-powered overviews</b> for any keyword and
        brand combination, providing strategic insights and actionable
        recommendations to help your brand leverage specific keywords
        effectively.
      </p>

      <div className="row">
        <div className="col-lg-9 col-md-10 col-sm-10">
          <HelpCard
            topic={"How To Use"}
            description={
              "AI Overview generates comprehensive strategic insights for any keyword and brand combination. It provides detailed analysis, recommendations, and actionable strategies to help your brand leverage specific keywords effectively for better market positioning and growth."
            }
            image={"/icon/pin.svg"}
          />
        </div>
      </div>
      <div className="p-2 mb-2 rounded">
        <div className="d-flex fw-bold mb-0">Generate AI Overview</div>
        <div className="row mb-2 align-items-end">
          <div className="mb-2">
            <p className="fw-normal justify-content-start align-items-center">
              Enter your target keyword, brand name, and number of queries to generate a
              comprehensive AI-powered overview.
            </p>
            <Form.Group>
              <div className="d-flex mt-2">
                <Col sm={3} className="me-2">
                  <Form.Control
                    type="text"
                    name="keyword"
                    placeholder="Enter keyword"
                    value={keyword}
                    onChange={handleInputChange}
                    isInvalid={!!errors.keyword}
                    onBlur={handleBlur}
                    required
                    className="px-2"
                    style={getInputFieldStyle('type6')}
                  />
                  {errors.keyword && (
                    <div className="text-danger small mt-1">
                      {errors.keyword}
                    </div>
                  )}
                </Col>
                <Col sm={3} className="me-2">
                  <Form.Control
                    type="text"
                    name="brand"
                    placeholder="Enter brand name (optional)"
                    value={brand}
                    onChange={handleInputChange}
                    isInvalid={!!errors.brand}
                    onBlur={handleBlur}
                    className="px-2"
                    style={getInputFieldStyle('type6')}
                  />
                  {errors.brand && (
                    <div className="text-danger small mt-1">{errors.brand}</div>
                  )}
                </Col>
                <Col sm={2} className="me-2">
                  <Form.Control
                    type="number"
                    name="noQueries"
                    placeholder="Number of queries"
                    value={noQueries}
                    onChange={handleInputChange}
                    isInvalid={!!errors.noQueries}
                    onBlur={handleBlur}
                    min="1"
                    max="50"
                    className="px-2"
                    style={getInputFieldStyle('type6')}
                  />
                  {errors.noQueries && (
                    <div className="text-danger small mt-1">{errors.noQueries}</div>
                  )}
                </Col>
                <div className="col-lg-4 col-md-4 col-sm-12 mb-2 me-1">
                  <div className="d-flex justify-content-start align-items-end">
                    <Button
                      variant="dark"
                      className="me-1"
                      onClick={handleSubmit}
                      disabled={!isFormValid || isLoading}
                      style={getButtonStyle('type6', 'primary')}
                    >
                      {isLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" />
                          Generating...
                        </>
                      ) : (
                        "Generate"
                      )}
                    </Button>
                    <Button
                      variant="outline-dark"
                      className="me-1"
                      onClick={handleReset}
                      disabled={isLoading}
                      style={getButtonStyle('type6', 'outline')}
                    >
                      Reset
                    </Button>
                  </div>
                </div>
              </div>
            </Form.Group>
          </div>
        </div>
      </div>

      <div className="p-2">
        {keyword && isResultGot && (
          <div className="d-flex justify-content-between align-items-center mb-3">
            <p className="d-flex fw-bold mb-0">
              AI Overview for
              <span className="search-item">
                &nbsp;"{keyword}"{brand ? ` - ${brand}` : ""}
              </span>
            </p>
          </div>
        )}
        {isLoading && <Loader />}

        {/* Table Display for List Responses */}
        {isResultGot && isListResponse && aiOverviewList.length > 0 && (
          <AiOverviewTable
            result={aiOverviewList}
            title="Generated AI Overview"
            onCopy={() => setShowCopyNotification(true)}
          />
        )}

      </div>

      {/* Insufficient Credits Popup */}
      <InsufficientCreditsPopup
        show={showInsufficientCredits}
        onClose={closeInsufficientCredits}
        balanceData={insufficientCreditsData}
      />
    </div>
  );
}
